import type { QuestRepository, QuestState } from "./types";
import { api } from "@/lib/api";

const STORAGE_KEY = "fitness-rpg:quest-state:v1";

export class MemoryQuestRepository implements QuestRepository {
  private state: QuestState | null;

  constructor(initialState: QuestState | null = null) {
    this.state = initialState;
  }

  async load(): Promise<QuestState | null> {
    return this.state ? cloneState(this.state) : null;
  }

  async save(state: QuestState): Promise<void> {
    this.state = cloneState(state);
  }

  async clear(): Promise<void> {
    this.state = null;
  }
}

export class LocalStorageQuestRepository implements QuestRepository {
  private readonly storage: Storage;
  private readonly key: string;

  constructor(storage: Storage = window.localStorage, key = STORAGE_KEY) {
    this.storage = storage;
    this.key = key;
  }

  async load(): Promise<QuestState | null> {
    const raw = this.storage.getItem(this.key);
    return raw ? (JSON.parse(raw) as QuestState) : null;
  }

  async save(state: QuestState): Promise<void> {
    this.storage.setItem(this.key, JSON.stringify(state));
  }

  async clear(): Promise<void> {
    this.storage.removeItem(this.key);
  }
}

export interface RemoteQuestStateClient {
  load(): Promise<{ state: QuestState | null; updatedAt: string | null }>;
  save(
    state: QuestState,
    expectedUpdatedAt?: string | null,
  ): Promise<{ state: QuestState; updatedAt: string }>;
}

export class CloudBackedQuestRepository implements QuestRepository {
  private remoteUpdatedAt: string | null = null;

  constructor(
    private readonly local: QuestRepository,
    private readonly remote: RemoteQuestStateClient,
  ) {}

  async load(): Promise<QuestState | null> {
    const localState = await this.local.load();
    try {
      const cloud = await this.remote.load();
      this.remoteUpdatedAt = cloud.updatedAt;
      const newest = newerQuestState(localState, cloud.state);
      if (newest && newest !== localState) {
        await this.local.save(newest);
      }
      return newest;
    } catch {
      return localState;
    }
  }

  async save(state: QuestState): Promise<void> {
    await this.local.save(state);
    try {
      const saved = await this.remote.save(state, this.remoteUpdatedAt);
      this.remoteUpdatedAt = saved.updatedAt;
    } catch {
      /* local save is authoritative while offline or if the cloud write fails */
    }
  }

  async clear(): Promise<void> {
    await this.local.clear();
  }
}

export function createBrowserQuestRepository(): QuestRepository {
  return new CloudBackedQuestRepository(new LocalStorageQuestRepository(), {
    load: () => api.questState(),
    save: (state, expectedUpdatedAt) =>
      api.saveQuestState(state, expectedUpdatedAt),
  });
}

function cloneState(state: QuestState): QuestState {
  return JSON.parse(JSON.stringify(state)) as QuestState;
}

function newerQuestState(
  localState: QuestState | null,
  remoteState: QuestState | null,
): QuestState | null {
  if (!localState) return remoteState ? cloneState(remoteState) : null;
  if (!remoteState) return localState;
  return Date.parse(remoteState.updatedAt) > Date.parse(localState.updatedAt)
    ? cloneState(remoteState)
    : localState;
}
